import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BASE_URL } from '../../components/apiConfig';
import { Trash2,ChevronLeft,CirclePlus } from 'lucide-react';
import { message } from 'antd';
import './kpi.css';

const KPIConfiguration = () => {
    const location = useLocation();
    const {clientID} = useParams();
    const {roleId} = useParams();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();
    const [kpiRows, setKpiRows] = useState([]);
    const [totalWeightage, setTotalWeightage] = useState(0);
    const [clientName, setClientName] = useState("");
    const { projectId } = useParams();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (location.state && location.state.clientName) {
            setClientName(location.state.clientName);
            console.log("Client Name:", location.state.clientName);
        }
    }, [location.state]);

    // Fetch existing KPIs when component mounts
    useEffect(() => {
        const fetchExistingKPIs = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("accessToken");
                
                const response = await fetch(`${BASE_URL}/kpi_project/get_kpis_of_project`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify({ project_id: projectId })
                });

                if (response.ok) {
                    const data = await response.json();
                    
                    if (data && data.length > 0) {
                        // Map the API response to your component's format
                        const mappedKPIs = data.map(kpi => ({
                            kpi_id: kpi.id,
                            name: kpi.kpi_name,
                            weightage: kpi.kpi_weight.toString(),
                            unit: kpi.kpi_unit === 1 ? "Unit" : "Revenue"
                        }));
                        
                        setKpiRows(mappedKPIs);
                    } else {
                        // No existing KPIs found, keep empty array
                        setKpiRows([]);
                    }
                } else {
                    console.error('Failed to fetch KPIs');
                    messageApi.error('Failed to load existing KPIs');
                }
            } catch (error) {
                console.error('Error fetching KPIs:', error);
                messageApi.error('Error loading existing KPIs');
            } finally {
                setLoading(false);
            }
        };

        if (projectId) {
            fetchExistingKPIs();
        }
    }, [projectId, messageApi]);

    useEffect(() => {
        const sum = kpiRows.reduce((total, row) => {
            const weight = parseFloat(row.weightage) || 0;
            return total + weight;
        }, 0);
        setTotalWeightage(sum);
    }, [kpiRows]);

    const addNewKPI = () => {
        setKpiRows([...kpiRows, {
            kpi_id: "",
            name: "",
            weightage: "",
            unit: "Select"
        }]);
    };

    const validateForm = () => {
        // Check if KPI rows exist
        if (kpiRows.length === 0) {
            messageApi.error('Please add at least one KPI.');
            return false;
        }

        // First check if any fields are empty
        const hasEmptyFields = kpiRows.some(row =>
            !row.name.trim() || !row.weightage.trim() || row.unit === "Select"
        );

        if (hasEmptyFields) {
            messageApi.error('Please fill in all KPI fields before proceeding.');
            return false;
        }

        // If all fields are filled, then check the weightage
        if (Math.abs(totalWeightage - 100) > 0.01) {
            messageApi.error('Total weightage must equal 100%. Current total: ' + totalWeightage + '%');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem("accessToken");
            const requestBody = {
                project_id: projectId,
                kpis: kpiRows.map(row => ({
                    kpi_name: row.name,
                    kpi_weight: parseFloat(row.weightage),
                    kpi_unit: row.unit === "Unit" ? 1 : 2
                }))
            };

            const response = await fetch(`${BASE_URL}/kpi_project/add_update_kpi_project`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok) {
                messageApi.error(data.detail || 'Failed to save KPI data.');
                return;
            }
            if(response.ok){
                navigate(`/architect/${clientID}/${roleId}/${projectId}/coloumnto_project`,{
                    state:{clientName}

                });
            }

            messageApi.success('KPI configuration saved successfully!');
            // Navigate or do further actions here
        } catch (error) {
            console.error('Error:', error);
            messageApi.error(`Failed to save KPI configuration: ${error.message}`);
        }
    };

    const handleWeightageChange = (index, value) => {
        // Allow only numbers and decimal point
        if (!/^\d*\.?\d*$/.test(value) && value !== '') {
            return;
        }

        const updatedRows = [...kpiRows];
        updatedRows[index].weightage = value;
        setKpiRows(updatedRows);
    };

    const handleUnitChange = (index, value) => {
        const updatedRows = [...kpiRows];
        updatedRows[index].unit = value;
        setKpiRows(updatedRows);
    };

    const removeKPI = (index) => {
        const updatedRows = [...kpiRows];
        updatedRows.splice(index, 1);
        setKpiRows(updatedRows);
    };

    const handleback = () => {
        navigate(`/architect/${clientID}/${roleId}/${projectId}/edit-project`, {
            state: { clientName } 
        })
    }

    // Show loading state while fetching
    if (loading) {
        return (
            <>
                {contextHolder}
                <div className="kpi-sidebar">
                </div>
                <div className="kpi-main-container">
                    <div className="kpi-container">
                        <div className="kpi-header">
                            <button className="kpi-back-button" onClick={handleback}><ChevronLeft/>
                             Key Performance Indicator (KPI) Configuration</button>
                        </div>
                        <div className="kpi-content">
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                Loading existing KPIs...
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {contextHolder}
            <div className="kpi-sidebar">
            </div>
            <div className="kpi-main-container">
                <div className="kpi-container">
                    <div className="kpi-header">
                        <button className="kpi-back-button" onClick={handleback}><ChevronLeft/>
                         Key Performance Indicator (KPI) Configuration</button>
                    </div>

                    <div className="kpi-content">
                        <div className="kpi-title-row">
                            <h3 className="kpi-distribution-title">KPI Distribution Table</h3>
                            <button className="kpi-add-button" onClick={addNewKPI}>
                                {/* <span className="kpi-add-icon"><CirclePlus size="16px"/></span> */}
                                 Add New KPI
                            </button>
                        </div>

                        {/* <div className="kpi-weightage-summary">
                            Total Weightage: <strong>{totalWeightage}%</strong> 
                            {Math.abs(totalWeightage - 100) > 0.01 && <span className="kpi-warning"> (Must equal 100%)</span>}
                        </div> */}

                        <div className="kpi-table-container">
                            <table className="kpi-table">
                                <thead>
                                    <tr>
                                        <th className="kpi-th">KPI</th>
                                        <th className="kpi-th">Weightage (%)</th>
                                        <th className="kpi-th">Unit</th>
                                        {/* <th className="kpi-th">Action</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {kpiRows.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="kpi-no-data">No Data Found</td>
                                        </tr>
                                    ) : (
                                        kpiRows.map((row, index) => (
                                            <tr key={index} className="kpi-row">
                                                <td className="kpi-td">
                                                    <input
                                                        type="text"
                                                        className="kpi-input"
                                                        value={row.name}
                                                        placeholder='KPI Name'
                                                        onChange={(e) => {
                                                            const updatedRows = [...kpiRows];
                                                            updatedRows[index].name = e.target.value;
                                                            setKpiRows(updatedRows);
                                                        }}
                                                    />
                                                </td>
                                                <td className="kpi-td">
                                                    <input
                                                        type="text"
                                                        className="kpi-input"
                                                        value={row.weightage}
                                                        placeholder='KPI Weightage'
                                                        onChange={(e) => handleWeightageChange(index, e.target.value)}
                                                    />
                                                </td>
                                                <td className="kpi-td1">
                                                    <div className="kpi-select-container">
                                                        <select
                                                            className="kpi-select"
                                                            value={row.unit}
                                                            onChange={(e) => handleUnitChange(index, e.target.value)}
                                                        >
                                                            <option value="Select" disabled>Select</option>
                                                            <option value="Unit">Quantity</option>
                                                            <option value="Revenue">Revenue</option>
                                                        </select>
                                                    </div>

                                                    <button
                                                        className="kpi-delete-button"
                                                        onClick={() => removeKPI(index)}
                                                        aria-label="Delete KPI"
                                                    >
                                                        <span className="kpi-delete-icon"> <Trash2 /> </span>
                                                    </button>
                                                </td>
                                                {/* <td className="kpi-td">
                                                    <button
                                                        className="kpi-remove-button"
                                                        onClick={() => removeKPI(index)}
                                                    >
                                                        ✕
                                                    </button>
                                                </td> */}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="kpi-footer">
                        <button
                            className="kpi-next-button"
                            onClick={handleSubmit}
                        >
                            Update
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default KPIConfiguration;